import React, { useState, useEffect, useRef } from 'react';
import {
  MapPin,
  X,
  Loader2,
  AlertCircle,
  SearchX,
} from 'lucide-react';
import { StructuredLocation, LocationAutocompleteSuggestion } from '../../types/location';
import { searchPlaceAutocomplete, fetchPlaceDetails } from '../../lib/locationService';

interface DynamicLocationSearchBoxProps {
  onLocationSelect: (location: StructuredLocation | null) => void;
  selectedLocation?: StructuredLocation | null;
  placeholder?: string;
  className?: string;
  showCoordinatesBadge?: boolean;
  showMapLink?: boolean;
  autoFocus?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

export const DynamicLocationSearchBox: React.FC<DynamicLocationSearchBoxProps> = ({
  onLocationSelect,
  selectedLocation = null,
  placeholder = 'Search your city, area or location',
  className = '',
  autoFocus = false,
  size = 'md',
}) => {
  const [inputValue, setInputValue] = useState<string>(
    selectedLocation ? selectedLocation.formatted_address || selectedLocation.city || '' : ''
  );
  const [suggestions, setSuggestions] = useState<LocationAutocompleteSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [currentLocation, setCurrentLocation] = useState<StructuredLocation | null>(selectedLocation);

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestSeqRef = useRef<number>(0);

  // Sync with prop updates
  useEffect(() => {
    if (selectedLocation) {
      setCurrentLocation(selectedLocation);
      setInputValue(selectedLocation.formatted_address || selectedLocation.city || '');
    } else if (selectedLocation === null && currentLocation) {
      setCurrentLocation(null);
      setInputValue('');
    }
  }, [selectedLocation]);

  // Cleanup pending timers and requests on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  // Handle outside clicks to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = (queryText: string) => {
    const trimmed = queryText.trim();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (trimmed.length < 2) {
      setSuggestions([]);
      setHasSearched(false);
      setSearchError(null);
      setIsOpen(false);
      setIsLoading(false);
      setActiveIndex(-1);
      return;
    }

    setIsLoading(true);
    setSearchError(null);
    setIsOpen(true);

    const currentSeq = ++requestSeqRef.current;
    debounceTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      try {
        const results = await searchPlaceAutocomplete(trimmed, controller.signal);
        if (currentSeq !== requestSeqRef.current || controller.signal.aborted) {
          return;
        }
        setSuggestions(results);
        setHasSearched(true);
        setActiveIndex(results.length > 0 ? 0 : -1);
        setIsOpen(true);
      } catch (err: any) {
        if (currentSeq !== requestSeqRef.current || controller.signal.aborted) {
          return;
        }
        console.warn('Location autocomplete search error:', err);
        setSuggestions([]);
        setHasSearched(true);
        setSearchError('Could not load locations. Please try again.');
        setIsOpen(true);
      } finally {
        if (currentSeq === requestSeqRef.current && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }, 220);
  };

  // Debounced autocomplete query
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    if (currentLocation && val !== currentLocation.formatted_address) {
      setCurrentLocation(null);
      onLocationSelect(null);
    }

    runSearch(val);
  };

  // User selects an autocomplete suggestion
  const handleSelectSuggestion = async (suggestion: LocationAutocompleteSuggestion) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setIsFetchingDetails(true);
    setIsOpen(false);
    setActiveIndex(-1);
    setSearchError(null);
    setInputValue(suggestion.description);

    const immediateFallback: StructuredLocation = {
      place_id: suggestion.place_id,
      formatted_address: suggestion.description,
      latitude: 22.5867,
      longitude: 88.4754,
      city: suggestion.main_text || suggestion.description.split(',')[0]?.trim() || 'Kolkata',
      state: 'India',
      country: 'India',
      country_code: 'IN',
    };

    try {
      const details = await fetchPlaceDetails(suggestion.place_id, suggestion);
      const resolved = details || immediateFallback;
      setCurrentLocation(resolved);
      setInputValue(resolved.formatted_address);
      onLocationSelect(resolved);
    } catch (err) {
      console.error('Error fetching place details:', err);
      setCurrentLocation(immediateFallback);
      onLocationSelect(immediateFallback);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' && suggestions.length > 0) {
        e.preventDefault();
        setIsOpen(true);
        setActiveIndex(0);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setActiveIndex((prev) => (prev + 1) % suggestions.length);
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (suggestions.length > 0) {
        setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      }
    } else if (e.key === 'Enter') {
      if (activeIndex >= 0 && activeIndex < suggestions.length) {
        e.preventDefault();
        handleSelectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setActiveIndex(-1);
    }
  };

  // Clear selected location
  const handleClear = () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
    setInputValue('');
    setSuggestions([]);
    setCurrentLocation(null);
    setHasSearched(false);
    setSearchError(null);
    setIsOpen(false);
    setActiveIndex(-1);
    onLocationSelect(null);
  };

  // Height / padding styling based on size
  const sizeClasses = {
    sm: 'py-2 pl-9 pr-8 text-xs rounded-xl',
    md: 'py-2.5 pl-10 pr-9 text-xs sm:text-sm rounded-xl',
    lg: 'py-3.5 pl-12 pr-10 text-sm sm:text-base rounded-2xl',
  }[size];

  const iconSizeClasses = {
    sm: 'w-3.5 h-3.5 left-2.5',
    md: 'w-4 h-4 left-3',
    lg: 'w-5 h-5 left-3.5',
  }[size];

  const trimmedQuery = inputValue.trim();

  return (
    <div ref={containerRef} className={`relative w-full ${className}`}>
      {/* Search Input Box */}
      <div className="relative flex items-center">
        <MapPin
          className={`absolute text-pink-600 pointer-events-none transition-colors ${iconSizeClasses} ${
            currentLocation ? 'text-pink-600 fill-pink-100' : 'text-slate-400'
          }`}
        />

        <input
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (trimmedQuery.length >= 2 && !currentLocation) {
              if (suggestions.length > 0 || hasSearched) {
                setIsOpen(true);
              } else {
                runSearch(trimmedQuery);
              }
            }
          }}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={`w-full bg-white border border-slate-200 text-slate-900 placeholder:text-slate-400 font-medium focus:outline-hidden focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-all shadow-2xs ${sizeClasses}`}
        />

        {/* Right Action Icons: Spinner or Clear Button */}
        <div className="absolute right-3 flex items-center gap-1.5">
          {isLoading || isFetchingDetails ? (
            <Loader2 className="w-4 h-4 text-pink-600 animate-spin" />
          ) : inputValue ? (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              title="Clear location"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      {/* Autocomplete Suggestions / Empty / Error Dropdown */}
      {isOpen && trimmedQuery.length >= 2 && (
        <div className="absolute z-[120] left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 max-h-72 overflow-y-auto">
          {isLoading && suggestions.length === 0 ? (
            <div className="px-4 py-3.5 flex items-center gap-2.5 text-xs font-medium text-slate-500">
              <Loader2 className="w-4 h-4 text-pink-600 animate-spin shrink-0" />
              <span>Searching matching locations...</span>
            </div>
          ) : searchError ? (
            <div className="px-4 py-3 flex items-center justify-between gap-2 text-xs text-rose-600 bg-rose-50/60">
              <span className="flex items-center gap-1.5 font-medium">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{searchError}</span>
              </span>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => runSearch(trimmedQuery)}
                className="px-2.5 py-1 rounded-lg bg-white border border-rose-200 text-rose-700 font-bold hover:bg-rose-100 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : suggestions.length > 0 ? (
            <ul role="listbox" className="divide-y divide-slate-100">
              {suggestions.map((item, idx) => {
                const isHighlighted = idx === activeIndex;
                return (
                  <li key={item.place_id} role="option" aria-selected={isHighlighted}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        // Prevent input blur before click selection fires on mobile WebView and desktop
                        e.preventDefault();
                      }}
                      onClick={() => handleSelectSuggestion(item)}
                      onMouseEnter={() => setActiveIndex(idx)}
                      className={`w-full text-left px-3.5 py-2.5 transition-colors flex items-start gap-2.5 cursor-pointer group ${
                        isHighlighted ? 'bg-pink-50/80' : 'hover:bg-pink-50/60'
                      }`}
                    >
                      <div
                        className={`mt-0.5 w-6 h-6 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                          isHighlighted
                            ? 'bg-pink-600 text-white'
                            : 'bg-pink-100 text-pink-600 group-hover:bg-pink-600 group-hover:text-white'
                        }`}
                      >
                        <MapPin className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold text-slate-900 truncate group-hover:text-pink-600 transition-colors">
                          {item.main_text}
                        </p>
                        <p className="text-[11px] text-slate-500 truncate">
                          {item.secondary_text || item.description}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : hasSearched ? (
            <div className="px-4 py-3.5 flex items-center gap-2.5 text-xs text-slate-500">
              <SearchX className="w-4 h-4 text-slate-400 shrink-0" />
              <span>No matching locations found for &ldquo;{trimmedQuery}&rdquo;. Try another city, area, or PIN code.</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
